import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { describeProviderResult, useAuth } from "../../contexts/AuthContext";
import { userService } from "../../services/userService";
import { usernameService } from "../../services/usernameService";
import { trainingTypeService } from "../../services/trainingTypeService";
import { analyticsService } from "../../services/analyticsService";
import { trackCompleteRegistration, trackInitiateCheckout } from "../../metaSignup";
import {
  completeOnboarding,
  createPlanCheckoutUrl,
  logFirstWellness,
  redeemInviteCode,
} from "../../services/onboardingService";
import { getAuthErrorMessage } from "../../utils/firebaseAuthErrors";
import FullScreenLoader from "../common/loadingPWA";
import {
  ONBOARDING_STEPS,
  OnboardingStep,
  useOnboardingWizard,
} from "../../hooks/useOnboardingWizard";
import { emailError, passwordError } from "./onboardingValidation";
import { COMPETITION_LEVELS, CompetitionLevel, FINISH_STATUS } from "./onboardingCopy";
import OnboardingFinishLoading from "./OnboardingFinishLoading";
import WelcomeStep from "./steps/WelcomeStep";
import CreateAccountStep from "./steps/CreateAccountStep";
import EmailStep from "./steps/EmailStep";
import InviteCodeStep from "./steps/InviteCodeStep";
import CompetitionLevelStep from "./steps/CompetitionLevelStep";
import SportsStep from "./steps/SportsStep";
import PersonalInfoStep from "./steps/PersonalInfoStep";
import GoalStep from "./steps/GoalStep";
import DifferenceStep from "./steps/DifferenceStep";
import FirstWellnessStep from "./steps/FirstWellnessStep";
import Day1Step from "./steps/Day1Step";
import NotificationsStep from "./steps/NotificationsStep";
import PlanStep, { PlanChoice } from "./steps/PlanStep";

const stepIndex = (step: OnboardingStep) => ONBOARDING_STEPS.indexOf(step);

const isPopupCancelled = (error: unknown) => {
  const code = (error as { code?: string } | undefined)?.code ?? "";
  return code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request" || code === "auth/user-cancelled";
};

/**
 * State-driven onboarding at /onboarding. Mirrors iOS `OnboardingFlowView` +
 * `OnboardingController`: Auth account is created mid-flow, the first
 * wellness log is written before the profile exists, and the profile docs are
 * written once at the end — here on the final plan step.
 */
const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    userData,
    authState,
    initializing,
    pendingTwoFactor,
    createAccountWithEmail,
    continueWithGoogle,
    continueWithApple,
    refreshUserData,
  } = useAuth();

  const checkUsername = useCallback(
    async (username: string) => {
      if (userData?.username && userData.username.toLowerCase() === username.toLowerCase()) return true;
      const result = await usernameService.checkUsernameAvailability(username);
      if (!result.valid) throw new Error(result.message);
      if (!result.available && result.message.startsWith("Unable")) throw new Error(result.message);
      return result.available;
    },
    [userData?.username],
  );

  const { state, dispatch, progress, canContinue, setUsername, goBack, continueTapped, clearDraft } =
    useOnboardingWizard({ checkUsername });

  const [planError, setPlanError] = useState<string | null>(null);
  const [inviteRedeeming, setInviteRedeeming] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [deepLinkConnecting, setDeepLinkConnecting] = useState(false);
  const leavingRef = useRef(false);
  const profileWrittenRef = useRef(false);
  const deepLinkHandled = useRef(false);
  const stepRef = useRef(state.step);
  stepRef.current = state.step;

  const codeFromLink = new URLSearchParams(location.search).get("code")?.trim() ?? "";

  // Coach invite emails land on /onboarding?code=... Prefill the field, and
  // once an account exists redeem it and skip the invite screen.
  const linkCodeApplied = useRef(false);
  useEffect(() => {
    if (!codeFromLink || linkCodeApplied.current) return;
    linkCodeApplied.current = true;
    if (!state.inviteCode) {
      dispatch({ type: "setField", field: "inviteCode", value: codeFromLink });
    }
  }, [codeFromLink, state.inviteCode]);

  const consumeInviteDeepLink = useCallback(async () => {
    if (!codeFromLink || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    setDeepLinkConnecting(true);
    try {
      await redeemInviteCode(codeFromLink);
      dispatch({ type: "setField", field: "inviteCode", value: codeFromLink });
    } catch (error) {
      console.error("Failed to redeem invite code from deep link:", error);
      dispatch({ type: "setField", field: "inviteCode", value: "" });
    } finally {
      setDeepLinkConnecting(false);
    }
  }, [codeFromLink]);

  useEffect(() => {
    if (!codeFromLink || !currentUser || deepLinkHandled.current || initializing) return;
    const signedUp =
      state.accountReady || state.existingProfile || authState.error === "User data not found" || Boolean(userData);
    if (!signedUp) return;

    (async () => {
      await consumeInviteDeepLink();
      // Deep links skip the invite screen. Don't pull someone backwards if
      // they have already moved on, and don't interrupt a finished profile.
      if (!userData?.onboardingComplete && stepRef.current === "inviteCode") {
        dispatch({ type: "goTo", step: "competitionLevel" });
      }
    })();
  }, [
    codeFromLink,
    currentUser,
    initializing,
    userData,
    state.accountReady,
    state.existingProfile,
    authState.error,
    consumeInviteDeepLink,
  ]);

  // Legacy Stripe return URLs (/onboarding?subscription_*) → dashboard.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("subscription_success") === "true" || params.get("subscription_canceled") === "true") {
      leavingRef.current = true;
      const next = new URLSearchParams();
      if (params.get("subscription_success") === "true") next.set("subscription_success", "true");
      if (params.get("subscription_canceled") === "true") next.set("subscription_canceled", "true");
      navigate(`/download?${next.toString()}`, { replace: true });
    }
  }, [location.search, navigate]);

  // Analytics: one event per step (state-driven, so no page_view per step).
  useEffect(() => {
    analyticsService.trackOnboarding(state.step, false);
  }, [state.step]);

  // Sports catalogue, loaded once on mount (iOS loads on flow appear).
  useEffect(() => {
    if (state.availableSports.length > 0 || state.sportsLoading) return;
    dispatch({ type: "sportsLoading" });
    trainingTypeService
      .fetchSports()
      .then((sports) => dispatch({ type: "sportsLoaded", sports }))
      .catch(() => dispatch({ type: "sportsFailed", message: "No sports available right now. Try again later." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconcile the draft with the live auth state.
  useEffect(() => {
    if (initializing || leavingRef.current || state.submitting) return;

    if (pendingTwoFactor) {
      leavingRef.current = true;
      navigate("/download?existing=1", { replace: true });
      return;
    }

    if (!currentUser) {
      // Auth user is gone (signed out elsewhere) but the draft thinks the
      // account exists — restart from the method picker with the answers kept.
      if (state.accountReady || state.existingProfile) {
        dispatch({
          type: "restore",
          draft: { accountReady: false, existingProfile: false, emailVerifiedByProvider: false, wellnessLogged: false, step: "createAccount" },
        });
      }
      return;
    }

    if (userData?.onboardingComplete) {
      leavingRef.current = true;
      clearDraft();
      navigate("/download", { replace: true });
      return;
    }

    if (userData && !userData.onboardingComplete) {
      // Legacy flow-B account: profile exists, onboarding never finished.
      if (!state.existingProfile) {
        const resumeStep: OnboardingStep =
          stepIndex(state.step) > stepIndex("competitionLevel") ? state.step : "competitionLevel";
        const level = COMPETITION_LEVELS.find((l) => l === userData.competitionLevel) ?? null;
        dispatch({
          type: "restore",
          draft: {
            existingProfile: true,
            accountReady: true,
            emailVerifiedByProvider: Boolean(userData.isEmailVerified),
            email: currentUser.email ?? userData.email ?? "",
            firstName: state.firstName || userData.name || "",
            username: state.username || userData.username || "",
            dob: userData.dob || state.dob,
            sex: (userData.gender === "female" || userData.gender === "male" ? userData.gender : state.sex),
            selectedSportIDs: state.selectedSportIDs.length ? state.selectedSportIDs : userData.trainingSportIDs ?? [],
            competitionLevel: state.competitionLevel ?? (level as CompetitionLevel | null),
            step: resumeStep,
          },
        });
        if (userData.username) dispatch({ type: "setUsernameStatus", status: { kind: "available" } });
      }
      return;
    }

    // Auth user with no users/{uid} doc yet: resume at the invite step.
    if (!state.accountReady && authState.error === "User data not found") {
      const providerVouched = currentUser.providerData.some((p) => p.providerId !== "password");
      dispatch({
        type: "accountCreated",
        email: currentUser.email ?? "",
        firstName: (currentUser.displayName ?? "").split(" ")[0],
        emailVerifiedByProvider: providerVouched,
        next: stepIndex(state.step) > stepIndex("inviteCode")
          ? state.step
          : codeFromLink
            ? "competitionLevel"
            : "inviteCode",
      });
    }
  }, [initializing, currentUser, userData, authState.error, pendingTwoFactor, state.accountReady, state.existingProfile, state.submitting]);

  // ── Auth actions ─────────────────────────────────────────────────────────────

  const createAccount = async () => {
    if (!state.legalAccepted) return;
    if (state.accountReady) {
      dispatch({ type: "goTo", step: codeFromLink ? "competitionLevel" : "inviteCode" });
      return;
    }
    const email = state.email.trim();
    const validation = emailError(email) ?? passwordError(state.password);
    if (validation) {
      dispatch({ type: "authError", message: validation });
      return;
    }
    if (state.authBusy) return;
    dispatch({ type: "authStart" });
    try {
      await createAccountWithEmail(email, state.password);
      trackCompleteRegistration();
      if (codeFromLink) await consumeInviteDeepLink();
      dispatch({
        type: "accountCreated",
        email,
        emailVerifiedByProvider: false,
        next: codeFromLink ? "competitionLevel" : "inviteCode",
      });
    } catch (error) {
      const code = (error as { code?: string }).code;
      const message =
        code === "auth/email-already-in-use"
          ? "An account with this email already exists. Download the app to continue."
          : getAuthErrorMessage(error);
      dispatch({ type: "authError", message });
    }
  };

  const continueWithProvider = async (method: "apple" | "google") => {
    if (!state.legalAccepted || state.authBusy) return;
    dispatch({ type: "authStart" });
    try {
      const raw = method === "apple" ? await continueWithApple() : await continueWithGoogle();
      if (!raw) {
        // 2FA challenge raised for an existing account — Login owns that UI.
        dispatch({ type: "authError", message: null });
        leavingRef.current = true;
        navigate("/download?existing=1", { replace: true });
        return;
      }
      const result = "isNewUser" in raw ? raw : describeProviderResult(raw);
      const existing = await userService.getUserData(result.user.uid).catch(() => null);
      if (existing) {
        // Existing Peak profile — this is a sign-in. The auth listener +
        // PrivateRoute route them (dashboard, or back here if incomplete).
        if (codeFromLink) await consumeInviteDeepLink();
        dispatch({ type: "authError", message: null });
        if (existing.onboardingComplete) {
          leavingRef.current = true;
          clearDraft();
          navigate("/download?existing=1", { replace: true });
        }
        return;
      }
      if (codeFromLink) await consumeInviteDeepLink();
      trackCompleteRegistration();
      dispatch({
        type: "accountCreated",
        email: result.user.email ?? "",
        firstName: result.firstName,
        emailVerifiedByProvider: true,
        next: codeFromLink ? "competitionLevel" : "inviteCode",
      });
    } catch (error) {
      dispatch({ type: "authError", message: isPopupCancelled(error) ? null : getAuthErrorMessage(error) });
    }
  };

  // ── Wellness ─────────────────────────────────────────────────────────────────

  const submitWellness = async () => {
    if (state.wellnessSubmitting) return;
    const uid = currentUser?.uid;
    if (state.wellnessLogged || !uid) {
      dispatch({ type: "goTo", step: "day1" });
      return;
    }
    dispatch({ type: "wellnessSubmitStart" });
    try {
      await logFirstWellness(uid, state.wellness);
      dispatch({ type: "wellnessSubmitDone" });
    } catch (error) {
      dispatch({ type: "wellnessSubmitError", message: error instanceof Error ? error.message : "Could not save your wellness log. Please try again." });
    }
  };

  // ── Finish ───────────────────────────────────────────────────────────────────

  const finish = async (plan: PlanChoice) => {
    if (state.submitting) return;
    const uid = currentUser?.uid;
    if (!uid) {
      setPlanError("Your session expired. Start signup again, or download the app if you already have an account.");
      dispatch({ type: "goTo", step: "createAccount" });
      return;
    }
    setPlanError(null);
    const onStatus = (status: string) => dispatch({ type: "submitStatus", status });
    dispatch({ type: "submitStart", status: FINISH_STATUS.profile });

    try {
      if (!profileWrittenRef.current) {
        await completeOnboarding(
          {
            uid,
            email: currentUser?.email ?? state.email,
            firstName: state.firstName,
            username: state.username,
            dob: state.dob,
            sex: state.sex,
            selectedSportIDs: state.selectedSportIDs,
            successLook: state.successLook,
            competitionLevel: state.competitionLevel,
            inviteCode: state.inviteCode,
            existingProfile: state.existingProfile,
          },
          { reminderHour: state.reminderHour, onStatus },
        );
        profileWrittenRef.current = true;
        analyticsService.trackProfileComplete(uid);
      }

      if (plan.type === "free") {
        analyticsService.trackOnboarding("complete", true);
        leavingRef.current = true;
        clearDraft();
        await refreshUserData();
        dispatch({ type: "submitDone" });
        navigate("/download?welcome=1", { replace: true });
        return;
      }

      onStatus(FINISH_STATUS.checkout);
      const fresh = await userService.getUserData(uid).catch(() => null);
      const checkoutUrl = await createPlanCheckoutUrl({
        uid,
        email: currentUser?.email ?? state.email,
        plan: { type: plan.type, name: plan.name, billingPeriod: plan.billingPeriod as "month" | "quarter" | "year" },
        validatedCountry: plan.validatedCountry,
        stripeCustomerId: fresh?.stripeCustomerId ?? userData?.stripeCustomerId ?? null,
        hasUsedTrial: fresh?.hasUsedTrial ?? userData?.hasUsedTrial,
        origin: window.location.origin,
      });
      analyticsService.trackOnboarding("complete", true);
      clearDraft();
      if (checkoutUrl) {
        leavingRef.current = true;
        if (plan.type === "plus" || plan.type === "premium") {
          trackInitiateCheckout({
            type: plan.type,
            name: plan.name,
            billingPeriod: plan.billingPeriod === "year" ? "year" : "month",
          });
        }
        window.location.href = checkoutUrl;
        return;
      }
      setPlanError("This plan is not available yet. You're on the free plan for now — upgrade any time from Settings.");
      leavingRef.current = true;
      await refreshUserData();
      dispatch({ type: "submitDone" });
      navigate("/download?welcome=1", { replace: true });
    } catch (error) {
      const message = error instanceof Error ? getAuthErrorMessage(error) : "Something went wrong finishing setup. Please try again.";
      dispatch({ type: "submitError", message });
      setPlanError(message);
    }
  };

  const applyInviteCode = async () => {
    const code = state.inviteCode.trim();
    if (!code || inviteRedeeming) return;
    setInviteRedeeming(true);
    setInviteError(null);
    try {
      await redeemInviteCode(code);
      dispatch({ type: "continue" });
    } catch (error) {
      const raw = error instanceof Error ? error.message.replace(/^Firebase:\s*/i, "").trim() : "";
      setInviteError(raw || "That code doesn't look right. Double check it and try again.");
    } finally {
      setInviteRedeeming(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (initializing) return <FullScreenLoader isLoading />;
  if (deepLinkConnecting) {
    return (
      <div className="flex min-h-[100dvh] w-full items-center justify-center bg-peak-bg px-6 text-center">
        <div>
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-peak-border border-t-peak-text" />
          <p className="font-funnel text-[17px] text-peak-text-secondary">Connecting you with your coach...</p>
        </div>
      </div>
    );
  }
  if (state.submitting) return <OnboardingFinishLoading status={state.submitStatus} />;

  const set = (field: "email" | "password" | "inviteCode" | "firstName" | "successLook" | "dob") => (value: string) =>
    dispatch({ type: "setField", field, value });

  switch (state.step) {
    case "welcome":
      return <WelcomeStep onContinue={continueTapped} onExistingAccount={() => navigate("/download?existing=1")} />;
    case "createAccount":
      return (
        <CreateAccountStep
          authBusy={state.authBusy}
          authError={state.authError}
          legalAccepted={state.legalAccepted}
          onLegalAcceptedChange={(accepted) => dispatch({ type: "setLegalAccepted", accepted })}
          onBack={goBack}
          onApple={() => continueWithProvider("apple")}
          onGoogle={() => continueWithProvider("google")}
          onEmail={() => dispatch({ type: "openEmail" })}
          onExistingAccount={() => navigate("/download?existing=1")}
        />
      );
    case "email":
      return (
        <EmailStep
          email={state.email}
          password={state.password}
          authBusy={state.authBusy}
          authError={state.authError}
          canContinue={canContinue("email")}
          legalAccepted={state.legalAccepted}
          onLegalAcceptedChange={(accepted) => dispatch({ type: "setLegalAccepted", accepted })}
          onEmailChange={set("email")}
          onPasswordChange={set("password")}
          onBack={goBack}
          onCreateAccount={createAccount}
        />
      );
    case "inviteCode":
      return (
        <InviteCodeStep
          inviteCode={state.inviteCode}
          canContinue={canContinue("inviteCode")}
          redeeming={inviteRedeeming}
          error={inviteError}
          onChange={(value) => {
            setInviteError(null);
            set("inviteCode")(value);
          }}
          onBack={goBack}
          onApply={applyInviteCode}
          onSkip={() => dispatch({ type: "skipInviteCode" })}
        />
      );
    case "competitionLevel":
      return (
        <CompetitionLevelStep
          value={state.competitionLevel}
          progress={progress}
          showBack={!state.existingProfile}
          onSelect={(value) => dispatch({ type: "selectCompetitionLevel", value })}
          onBack={goBack}
          onContinue={continueTapped}
        />
      );
    case "sports":
      return (
        <SportsStep
          sports={state.availableSports}
          selectedIDs={state.selectedSportIDs}
          loading={state.sportsLoading}
          loadError={state.sportsLoadError}
          progress={progress}
          onToggle={(id) => dispatch({ type: "toggleSport", id })}
          onBack={goBack}
          onContinue={continueTapped}
        />
      );
    case "sexAndDob":
      return (
        <PersonalInfoStep
          firstName={state.firstName}
          username={state.username}
          usernameStatus={state.usernameStatus}
          sex={state.sex}
          dob={state.dob}
          progress={progress}
          canContinue={canContinue("sexAndDob")}
          onFirstNameChange={set("firstName")}
          onUsernameChange={setUsername}
          onSexChange={(value) => dispatch({ type: "selectSex", value })}
          onDobChange={set("dob")}
          onBack={goBack}
          onContinue={continueTapped}
        />
      );
    case "success":
      return (
        <GoalStep
          value={state.successLook}
          progress={progress}
          canContinue={canContinue("success")}
          onChange={set("successLook")}
          onBack={goBack}
          onContinue={continueTapped}
          onSkip={() => dispatch({ type: "skipGoal" })}
        />
      );
    case "difference":
      return <DifferenceStep onBack={goBack} onContinue={continueTapped} />;
    case "howPeakHelps":
      return (
        <FirstWellnessStep
          wellness={state.wellness}
          submitting={state.wellnessSubmitting}
          submitError={state.wellnessSubmitError}
          alreadyLogged={state.wellnessLogged}
          onChange={(patch) => dispatch({ type: "updateWellness", patch })}
          onBack={goBack}
          onSubmit={submitWellness}
        />
      );
    case "day1":
      return <Day1Step onBack={goBack} onContinue={continueTapped} />;
    case "notifications":
      return (
        <NotificationsStep
          submitting={false}
          submitError={state.submitError}
          onBack={goBack}
          onFinish={(hour) => dispatch({ type: "setReminderHour", hour })}
        />
      );
    case "plan":
      return <PlanStep loading={state.submitting} error={planError ?? state.submitError} onBack={goBack} onSelect={finish} />;
    default:
      return null;
  }
};

export default OnboardingWizard;
